import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { namespaces as portalLibNamespaces } from '@frontend-libs/portal';

import { namespaces } from '@frontend/locales';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import { VolatileKeyValueStore } from '@frontend/utils';

export interface I18nProviderProps {
  children: ReactNode;
}

export interface I18nProviderState {
  loading: boolean;
  error: any | undefined;
  language: string;
  locale: Intl.Locale;
}

export interface I18nProviderValue {
  loading: boolean;
  error: any | undefined;
  language: string;
  locale: Intl.Locale;
  setLanguage(language: string): void;
}

const STORAGE_KEY = 'i18n_user_locale_preference';

const resolveDefaultLocale = async (
  portalDefaultLocale: string | undefined,
  supportedLocales: string[] | undefined,
) => {
  const preferredLocale = await VolatileKeyValueStore.get(STORAGE_KEY);

  return preferredLocale && supportedLocales?.includes(preferredLocale)
    ? preferredLocale
    : (portalDefaultLocale ?? 'en-US');
};

const I18nContext = createContext<I18nProviderValue>({
  setLanguage: () => {},
  error: undefined,
  language: 'en-GB',
  loading: true,
  locale: new Intl.Locale('en-GB'),
});

export const I18nProvider = (props: PropsWithChildren) => {
  const { defaultLocale, locales } = usePortalPublicConfiguration();

  const [state, setState] = useState<I18nProviderState>({
    loading: true,
    error: undefined,
    language: defaultLocale!,
    locale: new Intl.Locale(defaultLocale!),
  });

  useEffect(() => {
    if (i18next.isInitialized) {
      setState((prev) => ({
        ...prev,
        loading: false,
      }));
      return;
    }

    resolveDefaultLocale(defaultLocale, locales).then((locale) => {
      setState((prev) => ({
        ...prev,
        language: locale,
        locale: new Intl.Locale(locale),
      }));

      i18next.use(Backend).init(
        {
          ns: [...namespaces, ...portalLibNamespaces],
          load: 'currentOnly',
          fallbackLng: 'en-GB',
          lng: locale,
          backend: {
            loadPath: '/locales/{{lng}}/{{ns}}.json',
          },
        },
        (error) => {
          setState((prev) => ({
            ...prev,
            loading: false,
            error,
          }));
        },
      );
    });
  }, []);

  useEffect(() => {
    if (import.meta && import.meta.hot) {
      const onLocalesUpdate = () => {
        setState((prev) => ({
          ...prev,
          loading: true,
        }));

        i18next
          .reloadResources()
          .then(() => i18next.changeLanguage(i18next.language))
          .then(() => {
            setState((prev) => ({
              ...prev,
              loading: false,
            }));
          });
      };

      import.meta.hot.on('locales-update', onLocalesUpdate);

      return () => import.meta.hot?.off('locales-update', onLocalesUpdate);
    }

    return undefined;
  });

  return (
    <I18nContext.Provider
      value={{
        ...state,
        setLanguage(newLanguage) {
          if (newLanguage === state.language) {
            return;
          }

          i18next.changeLanguage(newLanguage);
          setState((prev) => ({
            ...prev,
            language: newLanguage,
            locale: new Intl.Locale(newLanguage),
          }));

          VolatileKeyValueStore.set(STORAGE_KEY, newLanguage);
        },
      }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nProviderValue => {
  const context = useContext(I18nContext);

  if (context === undefined) {
    throw new Error(`useI18n must be used in the context of an I18nProvider`);
  }

  return context;
};
